import { Attachments } from '@ant-design/x'
import { Link, useLocation } from '@remix-run/react'
import { Flex, Image, Skeleton } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { Api } from '~/core/trpc'
import { UIChatAttachment } from '../types'

type PropsOne = {
  attachment: UIChatAttachment
  attachmentSkeletonHeight?: number
  renderImage?: (attachment: UIChatAttachment, url: string) => React.ReactNode
  renderSkeleton?: () => React.ReactNode
}

const RenderAttachment: React.FC<PropsOne> = ({
  attachment,
  renderImage,
  renderSkeleton,
}) => {
  const location = useLocation()

  const filename = attachment.name ?? attachment.filename

  const fromPrivateToPublicUrl = Api.upload.fromPrivateToPublicUrl.useMutation()

  const fetchUrlPublic = async () => {
    if (
      (!attachment?.urlPrivate && !attachment.key) ||
      fromPrivateToPublicUrl.isError ||
      fromPrivateToPublicUrl.isSuccess ||
      fromPrivateToPublicUrl.isLoading
    ) {
      return
    }

    fromPrivateToPublicUrl.mutate({
      url: attachment.urlPrivate ?? attachment.url,
      key: attachment.key,
    })
  }

  useEffect(() => {
    fetchUrlPublic()
  }, [attachment.urlPrivate, attachment.key])

  const url = useMemo(() => {
    return fromPrivateToPublicUrl.data?.url
  }, [fromPrivateToPublicUrl.data])

  const urlLocal = useMemo(() => {
    if (attachment.driveFileId) {
      return `${location.pathname}/files/${attachment.driveFileId}`
    }
  }, [attachment.driveFileId])

  if (!urlLocal && !url) {
    if (fromPrivateToPublicUrl.isLoading) {
      if (renderSkeleton) {
        return renderSkeleton()
      } else {
        return (
          <Skeleton.Image active style={{ height: '100px', width: '100%' }} />
        )
      }
    }
  }

  const isImage = attachment.type?.includes('image')

  if (isImage) {
    return renderImage ? (
      renderImage(attachment, url ?? urlLocal)
    ) : (
      <Image
        src={url ?? urlLocal}
        wrapperStyle={{
          maxWidth: 100,
          maxHeight: 100,
          borderRadius: '16px',
          overflow: 'hidden',
        }}
        style={{ maxWidth: 100, maxHeight: 100, borderRadius: '16px' }}
      />
    )
  }

  if (urlLocal) {
    return (
      <Link to={urlLocal} style={{ width: '100%' }}>
        <Attachments.FileCard
          style={{ maxWidth: '500px', width: '100%' }}
          item={{
            uid: filename,
            name: filename,
            size: attachment.size,
            url,
            type: attachment.type,
          }}
        />
      </Link>
    )
  }

  return (
    <>
      <a href={url} target="_blank" rel="noreferrer" style={{ width: '100%' }}>
        <Attachments.FileCard
          style={{ maxWidth: '500px', width: '100%' }}
          item={{
            uid: filename,
            name: filename,
            size: attachment.size,
            url,
            type: attachment.type,
          }}
        />
      </a>
    </>
  )
}

type PropsMany = {
  attachments: UIChatAttachment[]
  isHorizontal?: boolean
  className?: string
  renderImage?: (attachment: UIChatAttachment, url: string) => React.ReactNode
  renderSkeleton?: () => React.ReactNode
}

export const RenderAttachments: React.FC<PropsMany> = ({
  attachments,
  isHorizontal = false,
  className = 'mt-4',
  renderImage,
  renderSkeleton,
}) => {
  return (
    <Flex vertical className={className}>
      <Flex gap={10} vertical={!isHorizontal} wrap>
        {attachments.map((attachment, index) => (
          <RenderAttachment
            key={`${index}-${attachment.name ?? attachment.filename}`}
            attachment={attachment}
            renderImage={renderImage}
            renderSkeleton={renderSkeleton}
          />
        ))}
      </Flex>
    </Flex>
  )
}
